import {css, StyleSheet} from 'aphrodite';
import React, {Dispatch, SetStateAction, useState} from 'react';

import {assert} from 'Asserts';
import {formatPolymerResidueCount} from 'biojs/Util';
import {Sequence} from 'components/Sequence';
import {SequenceTopology} from 'constants/SequenceConstants';
import {MethodData, Endpoints, BasicPolymerInfo} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {BulkAssemblyOrientation} from 'molbio/bulk-assembly/BulkAssemblyValues';
import FragmentCardContents, {
  FragmentModalFragment,
} from 'molbio/bulk-assembly/polymer-map-modals/FragmentCardContents';
import useFragmentDataSetters from 'molbio/bulk-assembly/polymer-map-modals/hooks/useFragmentDataSetters';
import {
  useMirroringFromPolymerMapSelection,
  useMirroringFromFragmentEndpoints,
} from 'molbio/bulk-assembly/polymer-map-modals/hooks/useSelectionMirroring';
import useTransientCurrentPolymerModel from 'molbio/bulk-assembly/polymer-map-modals/hooks/useTransientCurrentPolymerModel';
import {PolymerModalContent} from 'molbio/bulk-assembly/polymer-map-modals/PolymerModalContent';
import {PolymerTitle} from 'molbio/bulk-assembly/polymer-map-modals/PolymerTitle';
import ProteinMapContainer from 'molbio/bulk-assembly/polymer-map-modals/ProteinMapContainer';
import SequenceMapContainer, {
  AssemblySequenceType,
} from 'molbio/bulk-assembly/polymer-map-modals/SequenceMapContainer';
import {
  getPreferredRestrictionEnzymeId,
  useRestrictionEnzymeInfo,
  areEnzymesRequired,
} from 'molbio/bulk-assembly/util/EnzymeUtil';
import {
  getBasicFragmentSelectionDetails,
  validateEndpoints,
} from 'molbio/bulk-assembly/util/FragmentEndpointUtil';
import {GRAY_DARKER} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export interface FragmentModalContentProps {
  /** ID of the assembly. Used for analytics. */
  assemblyId: string;
  /** Method metadata for the assembly being performed. */
  bulkAssemblyMethod: MethodData;
  /** Whether the user can specify endpoints for a fragment */
  canSpecifyEndpoints: boolean;
  /** Array of fragment data. */
  fragmentsData: Array<FragmentModalFragment>;
  /** Callback to update the array of fragment data. */
  setFragmentsData: Dispatch<SetStateAction<Array<FragmentModalFragment>>>;
  /** Whether the fragment should be displayed in a read-only state, e.g. when the assembly is finalized */
  isReadOnly: boolean;
  /* Topology of the constructs that will be assembled. */
  constructTopology: SequenceTopology;
  numConstructFragments: number;
}

const PolymerFragmentCondensedCard = ({
  polymerId,
  endpoints,
  orientation,
  canSpecifyEndpoints,
  basicPolymerInfo,
}: {
  polymerId: string;
  endpoints: Endpoints;
  orientation: BulkAssemblyOrientation;
  canSpecifyEndpoints: boolean;
  basicPolymerInfo?: BasicPolymerInfo;
}): JSX.Element => {
  let fragmentLengthDetails: string;
  if (basicPolymerInfo == null) {
    fragmentLengthDetails = 'Loading...';
  } else {
    const {length} = getBasicFragmentSelectionDetails(
      endpoints,
      orientation,
      basicPolymerInfo,
      canSpecifyEndpoints
    );
    fragmentLengthDetails = formatPolymerResidueCount(length, basicPolymerInfo.residueType);
  }

  return (
    <>
      <div className={css(styles.condensedCardHeader)}>
        <PolymerTitle polymerId={polymerId} />
        <BenchlingIcon className={css(styles.chevronIcon)} icon="chevron-down" color={GRAY_DARKER} />
      </div>
      <div>{fragmentLengthDetails}</div>
    </>
  );
};

export default function FragmentsModalContent({
  assemblyId,
  bulkAssemblyMethod,
  canSpecifyEndpoints,
  fragmentsData,
  setFragmentsData,
  isReadOnly,
  constructTopology,
  numConstructFragments,
}: FragmentModalContentProps): JSX.Element {
  assert(fragmentsData.length > 0, 'Expected at least one fragment');
  const [activeIndex, setActiveIndex] = useState(0);
  const fragmentDataSetters = useFragmentDataSetters(setFragmentsData, assemblyId, activeIndex);

  const activeFragment = fragmentsData[activeIndex];
  const polymer = useTransientCurrentPolymerModel(activeFragment.polymerId);
  const preferredRestrictionEnzymeInfo = useRestrictionEnzymeInfo(
    getPreferredRestrictionEnzymeId(bulkAssemblyMethod),
    activeFragment.basicPolymerInfo
  );
  const restrictionEnzymeInfo5 = useRestrictionEnzymeInfo(
    activeFragment.restrictionEnzymeId5,
    activeFragment.basicPolymerInfo
  );
  const restrictionEnzymeInfo3 = useRestrictionEnzymeInfo(
    activeFragment.restrictionEnzymeId3,
    activeFragment.basicPolymerInfo
  );
  const areRestrictionEnzymesRequired = areEnzymesRequired(
    activeFragment.constructFragmentIndex,
    preferredRestrictionEnzymeInfo,
    constructTopology,
    numConstructFragments,
    activeFragment.fragmentProductionMethod
  );

  const {canMirrorToMap, startErrorMessage, endErrorMessage} = validateEndpoints(
    activeFragment.endpoints,
    activeFragment.basicPolymerInfo.residues.length,
    activeFragment.basicPolymerInfo.startIndex,
    activeFragment.orientation,
    restrictionEnzymeInfo5,
    restrictionEnzymeInfo3,
    canSpecifyEndpoints,
    activeFragment.basicPolymerInfo.circular,
    areRestrictionEnzymesRequired
  );

  useMirroringFromFragmentEndpoints({
    polymer,
    canUpdateRestrictionEnzymes: activeFragment.canUpdateRestrictionEnzymes,
    canSpecifyEndpoints,
    isReadOnly,
    restrictionEnzymeInfo5,
    restrictionEnzymeInfo3,
    endpoints: activeFragment.endpoints,
    orientation: activeFragment.orientation,
    canMirrorToMap,
  });
  useMirroringFromPolymerMapSelection({
    maybeSelection: polymer?.selection(),
    setEndpoints: fragmentDataSetters.setEndpoints,
    setRestrictionEnzymeId5: fragmentDataSetters.setRestrictionEnzymeId5,
    setRestrictionEnzymeId3: fragmentDataSetters.setRestrictionEnzymeId3,
    canUpdateRestrictionEnzymes: activeFragment.canUpdateRestrictionEnzymes,
  });

  let activePolymerMap: JSX.Element;
  if (polymer == null) {
    // Render throbber while Backbone model is being created.
    activePolymerMap = <CenteredHorizontalThrobber />;
  } else if (polymer instanceof Sequence) {
    activePolymerMap = (
      <SequenceMapContainer
        sequence={polymer}
        bulkAssemblyMethod={bulkAssemblyMethod.type}
        type={AssemblySequenceType.FRAGMENT}
      />
    );
  } else {
    activePolymerMap = <ProteinMapContainer protein={polymer} />;
  }
  return (
    <PolymerModalContent
      assemblyId={assemblyId}
      activeCardSize={400}
      activeIndex={activeIndex}
      inactiveCardSize={100}
      setActiveIndex={setActiveIndex}
      cards={fragmentsData.map((fragmentData) => ({
        condensedContent: (
          <PolymerFragmentCondensedCard
            polymerId={fragmentData.polymerId}
            endpoints={fragmentData.endpoints}
            orientation={fragmentData.orientation}
            canSpecifyEndpoints={canSpecifyEndpoints}
            basicPolymerInfo={fragmentData.basicPolymerInfo}
          />
        ),
        content: (
          <FragmentCardContents
            assemblyId={assemblyId}
            canSpecifyEndpoints={canSpecifyEndpoints}
            {...fragmentData}
            {...fragmentDataSetters}
            isReadOnly={isReadOnly}
            validationFailureMessage={startErrorMessage ?? endErrorMessage}
            restrictionEnzymeInfo5={restrictionEnzymeInfo5}
            restrictionEnzymeInfo3={restrictionEnzymeInfo3}
          />
        ),
        polymerId: fragmentData.polymerId,
      }))}
      activePolymerMap={activePolymerMap}
    />
  );
}

const styles = StyleSheet.create({
  condensedCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: '8px',
  },
  chevronIcon: {
    alignSelf: 'center',
  },
});
